import { FileText } from "lucide-react";

export default function Terms() {
  return (
    <main data-testid="terms-page" className="pt-24 md:pt-32 pb-20">
      <div className="max-w-screen-md mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-400 rounded-xl flex items-center justify-center shadow-lg shadow-purple-500/30">
              <FileText className="w-6 h-6 text-white" />
            </div>
            <span className="text-xs font-medium text-purple-400 uppercase tracking-widest">
              Documento Legale
            </span>
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight mb-4">
            Termini di Servizio
          </h1>
          <p className="text-purple-200/50">
            Ultimo aggiornamento: Gennaio 2026
          </p>
        </div>

        {/* Content */}
        <div className="space-y-10">
          <section className="legal-section" data-testid="terms-acceptance">
            <h2 className="text-2xl font-bold text-white mb-4">
              1. Accettazione dei Termini
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Accedendo o utilizzando l'app Nerbys (l'"App") accetti i presenti Termini di Servizio (i "Termini"). 
                Se non intendi accettarli, non utilizzare l'App.
              </p>
              <p>
                Il titolare dell'App è <strong className="text-white">Giuseppe D'Ambra</strong> ("Nerbys", "noi"). 
                Possiamo modificare i Termini per ragioni legali, di sicurezza o di evoluzione del Servizio. 
                In caso di modifiche rilevanti ti informeremo tramite App e/o email con un ragionevole preavviso. 
                Continuando a usare l'App dopo l'entrata in vigore delle modifiche, accetti i Termini aggiornati; 
                se non li accetti, puoi interrompere l'uso e cancellare l'account.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-description">
            <h2 className="text-2xl font-bold text-white mb-4">
              2. Descrizione del Servizio
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Nerbys è una piattaforma di social discovery basata sulla prossimità che consente agli utenti 
                di individuare e connettersi con persone nelle vicinanze.
              </p>
              <p className="text-white font-semibold">Funzionalità principali:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>scansione proximity-based per individuare utenti vicini (indicativamente entro 50 metri);</li>
                <li>profilo utente (foto, bio, segni distintivi facoltativi);</li>
                <li>collegamento di almeno un profilo social esterno (link);</li>
                <li>profilo Pubblico o Privato (gestione visibilità dei link social);</li>
                <li>stato temporaneo "In questo momento" (durata 4 ore);</li>
                <li>notifiche per richieste e connessioni.</li>
              </ul>
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-4 mt-4">
                <p className="text-purple-300">
                  <strong>Nota:</strong> Nerbys non offre chat interna: le interazioni avvengono tramite richieste/connessioni 
                  e, se l'utente lo consente, tramite i link social condivisi.
                </p>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-eligibility">
            <h2 className="text-2xl font-bold text-white mb-4">
              3. Requisiti di utilizzo e idoneità
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>Per usare l'App devi:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>avere almeno <strong className="text-yellow-400">17 anni</strong>;</li>
                <li>creare un account personale e non trasferibile;</li>
                <li>fornire informazioni veritiere e non fuorvianti;</li>
                <li>collegare almeno un account social esterno tramite link (vedi art. 6);</li>
                <li>utilizzare un dispositivo compatibile iOS/Android e mantenere aggiornato il software;</li>
                <li>concedere i permessi necessari al funzionamento (vedi art. 4).</li>
              </ul>
              <p className="mt-4">
                Ci riserviamo il diritto di sospendere o chiudere account che violino i presenti Termini 
                o che pongano rischi per la sicurezza della community.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-permissions">
            <h2 className="text-2xl font-bold text-white mb-4">
              4. Permessi e funzionamento basato sulla posizione
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>L'App utilizza la posizione per la funzionalità di discovery.</p>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-5 space-y-4">
                <div>
                  <p className="text-white font-semibold mb-2">4.1 Posizione "durante l'uso"</p>
                  <p>
                    Quando apri l'App e avvii la scansione, l'App richiede l'accesso alla posizione "durante l'uso" 
                    per individuare utenti nelle vicinanze.
                  </p>
                </div>
                
                <div>
                  <p className="text-white font-semibold mb-2">4.2 Posizione "in background" e visibilità quando l'App è chiusa</p>
                  <p>
                    Per consentire agli altri utenti di trovarti anche quando l'App è chiusa, Nerbys può richiedere 
                    (se disponibile e se da te autorizzato) l'accesso alla posizione in background. 
                    Puoi in qualsiasi momento revocare o modificare i permessi di localizzazione dalle impostazioni 
                    del dispositivo; la revoca può limitare o impedire la discovery e la tua visibilità quando l'App è chiusa.
                  </p>
                </div>
                
                <div>
                  <p className="text-white font-semibold mb-2">4.3 Altri permessi</p>
                  <ul className="list-disc list-inside space-y-1 ml-4">
                    <li><strong className="text-white">Fotocamera / Libreria foto:</strong> per caricare o scattare la foto profilo.</li>
                    <li><strong className="text-white">Notifiche push:</strong> per avvisi su richieste e connessioni (disattivabili da impostazioni dispositivo).</li>
                  </ul>
                </div>
              </div>
              
              <p className="mt-4">
                I dettagli sul trattamento dei dati personali (incluse conservazione e basi giuridiche) sono indicati nella Privacy Policy.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-conduct">
            <h2 className="text-2xl font-bold text-white mb-4">
              5. Regole di comportamento e contenuti vietati
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                È vietato utilizzare l'App per scopi illegali o in modo da compromettere sicurezza e fiducia della community. 
                In particolare è vietato:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>creare profili falsi o ingannevoli, impersonare terzi;</li>
                <li>caricare contenuti illegali, offensivi, minacciosi, molesti, discriminatori o che incitino all'odio/violenza;</li>
                <li>caricare contenuti sessualmente espliciti o pornografici;</li>
                <li>pubblicare dati personali di terzi senza autorizzazione (doxxing) o raccogliere dati di altri utenti senza consenso;</li>
                <li>usare foto di altre persone senza il loro consenso o diritti necessari;</li>
                <li>molestare, intimidire, ricattare o perseguire altri utenti (stalking);</li>
                <li>utilizzare bot, scraping, automazioni non autorizzate o tentare di aggirare i sistemi di sicurezza;</li>
                <li>spam, promozioni o attività commerciali non autorizzate.</li>
              </ul>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-social">
            <h2 className="text-2xl font-bold text-white mb-4">
              6. Collegamento di account social (requisito)
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Per ridurre l'uso passivo e migliorare la tracciabilità e l'affidabilità degli account, 
                per usare la funzione di scansione devi collegare almeno un profilo social esterno.
              </p>
              <p>
                Il collegamento avviene tramite inserimento del link o identificativo del profilo. 
                Nerbys non richiede e non raccoglie le credenziali di accesso ai social e non accede 
                ai contenuti del tuo account social se non tramite ciò che è pubblicamente disponibile sul relativo social.
              </p>
              <p>
                Se inserisci link o riferimenti a profili social, garantisci di avere diritto a farlo 
                e che tali informazioni non violano diritti di terzi.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-privacy-modes">
            <h2 className="text-2xl font-bold text-white mb-4">
              7. Modalità privacy del profilo (Pubblico / Privato)
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  <strong className="text-white">Profilo Pubblico:</strong> i link ai social collegati sono visibili 
                  immediatamente agli utenti che ti individuano nella scansione.
                </li>
                <li>
                  <strong className="text-white">Profilo Privato:</strong> gli altri utenti devono inviarti una richiesta 
                  di connessione; solo dopo la tua accettazione potranno vedere i tuoi link social.
                </li>
              </ul>
              <p className="mt-4">
                Puoi modificare l'impostazione in qualsiasi momento dalle impostazioni dell'App. La modifica ha effetto immediato.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-photo-verification">
            <h2 className="text-2xl font-bold text-white mb-4">
              8. Verifica foto profilo (controllo automatizzato "umano/non umano")
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Per ridurre profili falsi, Nerbys utilizza sistemi automatizzati che analizzano la foto profilo 
                al solo fine di verificare che raffiguri un soggetto umano e che non sia manifestamente in contrasto 
                con le regole della community.
              </p>
              <p>
                Foto non conformi possono essere rifiutate o richiedere sostituzione. 
                In caso di contestazione, l'utente può richiedere un riesame scrivendo a 
                <a href="mailto:support@nerbys.app" className="text-purple-400 hover:underline ml-1">support@nerbys.app</a>.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-moderation">
            <h2 className="text-2xl font-bold text-white mb-4">
              9. Segnalazioni, blocchi e moderazione
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                L'App mette a disposizione strumenti per segnalare e bloccare utenti. 
                Possiamo intervenire su account e contenuti in caso di violazioni.
              </p>
              
              <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-6">
                <p className="text-white font-semibold mb-4">9.1 Sistema warning</p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0 text-yellow-400 font-bold text-sm">1°</span>
                    <span><strong className="text-white">warning:</strong> avviso</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 bg-orange-500/20 rounded-lg flex items-center justify-center flex-shrink-0 text-orange-400 font-bold text-sm">2°</span>
                    <span><strong className="text-white">warning:</strong> secondo avviso</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-8 h-8 bg-red-500/20 rounded-lg flex items-center justify-center flex-shrink-0 text-red-400 font-bold text-sm">3°</span>
                    <span><strong className="text-white">warning:</strong> sospensione temporanea (es. 10 giorni)</span>
                  </li>
                </ul>
                <p className="mt-4 text-red-400">
                  <strong>Violazioni gravi</strong> (es. minacce, molestie, stalking, contenuti illegali) possono comportare 
                  <strong> ban permanente immediato</strong>, senza necessità di warning progressivi.
                </p>
              </div>
              
              <div className="mt-4">
                <p className="text-white font-semibold mb-2">9.2 Contestazioni</p>
                <p>
                  Se ritieni che un provvedimento (rimozione contenuti, sospensione, ban) sia stato adottato per errore, 
                  puoi contestarlo scrivendo a <a href="mailto:support@nerbys.app" className="text-purple-400 hover:underline">support@nerbys.app</a> indicando 
                  email account, nickname e motivazione.
                </p>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-content-license">
            <h2 className="text-2xl font-bold text-white mb-4">
              10. Contenuti degli utenti e licenza
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                I contenuti che pubblichi o carichi nell'App (es. foto profilo, bio, "In questo momento") restano di tua proprietà.
              </p>
              <p>
                Tuttavia, per poter fornire il Servizio, concedi a Nerbys una licenza non esclusiva, mondiale, gratuita, 
                trasferibile e sublicenziabile per fini tecnici, limitata a ospitare, riprodurre, mostrare e rendere disponibili 
                tali contenuti nell'ambito del Servizio e per finalità di sicurezza e moderazione.
              </p>
              <p>
                Garantisci di possedere i diritti necessari sui contenuti caricati e che gli stessi non violano diritti di terzi.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-intellectual-property">
            <h2 className="text-2xl font-bold text-white mb-4">
              11. Proprietà intellettuale dell'App
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Il marchio Nerbys, il logo, il design e i contenuti originali dell'App sono di proprietà di 
                <strong className="text-white"> Giuseppe D'Ambra</strong>.
              </p>
              <p>
                Ti concediamo una licenza limitata, non esclusiva, non trasferibile e revocabile per utilizzare l'App 
                a uso personale e non commerciale, nel rispetto dei Termini.
              </p>
              <p>
                È vietato copiare, modificare, distribuire, decompilare o tentare di ricavare il codice sorgente dell'App, 
                salvo quanto consentito dalla legge.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-limitation">
            <h2 className="text-2xl font-bold text-white mb-4">
              12. Limitazione di responsabilità
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Nerbys fornisce un servizio di discovery e connessione tra utenti. Nei limiti consentiti dalla legge:
              </p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>non garantiamo che l'App sia sempre disponibile o priva di errori;</li>
                <li>non siamo responsabili per condotte illecite o scorrette degli utenti, né per interazioni fuori dall'App;</li>
                <li>non assumiamo responsabilità per contenuti forniti dagli utenti, fermo restando il nostro diritto di intervenire in moderazione.</li>
              </ul>
              <p className="mt-4">
                Nulla in questi Termini limita o esclude la responsabilità per dolo o colpa grave, 
                o per altri casi in cui la limitazione sia vietata dalla legge.
              </p>
              <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4 mt-4">
                <p className="text-yellow-400/90">
                  <strong>Avvertenza di sicurezza:</strong> incontri e contatti nella vita reale comportano rischi. 
                  Ti invitiamo a usare prudenza e a incontrare nuove persone in luoghi pubblici.
                </p>
              </div>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-cancellation">
            <h2 className="text-2xl font-bold text-white mb-4">
              13. Cancellazione dell'account
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                Puoi cancellare l'account in qualsiasi momento dalle impostazioni dell'App ("Elimina account"). 
                In alternativa, puoi richiedere assistenza inviando una email a 
                <a href="mailto:privacy@nerbys.app" className="text-purple-400 hover:underline ml-1">privacy@nerbys.app</a>.
              </p>
              <p>
                La cancellazione comporta la rimozione del profilo dalla discovery e la disattivazione dell'account. 
                Alcuni dati possono essere conservati per un periodo limitato per obblighi di legge, sicurezza e 
                prevenzione abusi, come indicato nella Privacy Policy.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-governing-law">
            <h2 className="text-2xl font-bold text-white mb-4">
              14. Legge applicabile e foro
            </h2>
            <div className="space-y-4 text-purple-200/70 text-sm leading-relaxed">
              <p>
                I presenti Termini sono regolati dalla legge italiana. Per le controversie con consumatori, 
                il foro competente è quello del luogo di residenza o domicilio del consumatore, ai sensi del Codice del Consumo.
              </p>
            </div>
          </section>

          <section className="legal-section" data-testid="terms-contact">
            <h2 className="text-2xl font-bold text-white mb-4">
              15. Contatti
            </h2>
            <div className="bg-purple-950/30 border border-purple-500/10 rounded-xl p-6">
              <p className="text-white font-bold mb-3">Nerbys – Giuseppe D'Ambra</p>
              <div className="space-y-2">
                <p className="text-purple-200/70">
                  <strong className="text-white">Supporto:</strong> <a href="mailto:support@nerbys.app" className="text-purple-400 hover:underline">support@nerbys.app</a>
                </p>
                <p className="text-purple-200/70">
                  <strong className="text-white">Privacy:</strong> <a href="mailto:privacy@nerbys.app" className="text-purple-400 hover:underline">privacy@nerbys.app</a>
                </p>
              </div>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
